




























































/* General */
import Vue from 'vue'
import { debounce } from 'lodash'

/* Mixins */

/* Components */

/* Helpers */
import ItemFilterAssistant from '@/helpers/item-filter-assistant'
import SortAssistant from '@/helpers/sort-assistant'
import AssigneeAssistant from '@/helpers/assignee-assistant'

/* Data types */
import { IItemForSelection, IPersonBase } from '@/api/types/response.types'
import { IItemsInputJobAppointeeGrouping } from '@/api/types/request.types'
import avatarGallery from '@/components/global/avatar-gallery.vue'
import Avatar from '@/components/global/avatar.vue'

/* API */

export default Vue.extend({
  name: 'IfpFilterJobs',
  components: { avatarGallery, Avatar },

  props: {
    jobOptions: {
      type: Array as () => IItemForSelection[],
      required: true,
    },
    appointeeOptions: {
      type: Array as () => IPersonBase[],
      required: true,
    },
    selectedJobAppointeeGroupingsString: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      debounceEmitSelectedJobAppointeeGroupings: null,
      selectedJobName: null,
      internalSelectedJobAppointeeGroupings: [] as IItemsInputJobAppointeeGrouping[],
      emitDebounceTimeInMS: 500,
    }
  },

  computed: {
    // If showing multiple workflows, job names can overlap. Let's extract
    // distinct names (using the first instance if multiple).
    displayableJobOptions(): IItemForSelection[] {
      return this.jobOptions.filter(
        (j, i, arr) =>
          arr.findIndex(
            t => t.name.toLowerCase().trim() === j.name.toLowerCase().trim(),
          ) === i,
      )
    },
    curatedAppointeeOptions(): IPersonBase[] {
      return AssigneeAssistant.getFullAssigneeOptions(
        this.appointeeOptions,
        this.$permissions,
      )
    },
    jobsLabel(): string {
      if (this.hasOnlyOneJob) {
        /* Doing this so that the non-lower-cased job name is surfaced.
           Also, there is an edge-case where, if the job name has been changed
           by the user, then we don't get any matches because we are storing the
           filter by the job name. In those cases, we'll always be returning no record
           matches. But, for the purposes of the label, we will just show "Jobs" and the 
           requisite appointee avatars. Not ideal, but it's an edge case (and it doesn't
           break the app). */
        const loneJobName = this.internalSelectedJobAppointeeGroupings[0]
          .jobName

        const matchingJobOption = this.jobOptions.find(
          jo => jo.name.toLowerCase().trim() === loneJobName,
        )

        if (matchingJobOption) {
          return matchingJobOption.name
        }
      }
      return 'Jobs'
    },
    hasOnlyOneJob(): boolean {
      return this.internalSelectedJobAppointeeGroupings.length === 1
    },
  },

  watch: {
    selectedJobAppointeeGroupingsString: {
      immediate: true,
      handler(val) {
        this.internalSelectedJobAppointeeGroupings = ItemFilterAssistant.convertStringToJobAppointeeGroupingsRepresentation(
          val,
        )
      },
    },
  },

  created() {
    this.debounceEmitSelectedJobAppointeeGroupings = debounce(
      this.emitSelectedJobAppointeeGroupingsString,
      this.emitDebounceTimeInMS,
    )
  },

  methods: {
    updateSelectedJobAppointeeGroupingsString(
      jagString: string,
      emitImmediately?: boolean,
    ) {
      if (emitImmediately) {
        this.emitSelectedJobAppointeeGroupingsString(jagString)
      } else {
        this.debounceEmitSelectedJobAppointeeGroupings(jagString)
      }
    },
    emitSelectedJobAppointeeGroupingsString(jagString: string) {
      this.$emit('selected-job-appointee-groupings-string-updated', jagString)
    },
    handleClearClick() {
      this.updateSelectedJobAppointeeGroupingsString('', true)
    },
    sortGroupingsDataChronologically(
      groupings: IItemsInputJobAppointeeGrouping[],
    ): IItemsInputJobAppointeeGrouping[] {
      return groupings.map(
        g =>
          ({
            jobName: g.jobName,
            listAppointeeIDs: SortAssistant.sortChronologically(
              g.listAppointeeIDs,
            ),
          } as IItemsInputJobAppointeeGrouping),
      )
    },
    hasAppointeesForJobName(jobName: string): boolean {
      return this.getAppointeesForJobName(jobName).length > 0
    },
    getAppointeesForJobName(jobName: string): IPersonBase[] {
      let result = [] as IPersonBase[]
      const relevantJobAppointeeGrouping = this.getJobAppointeeGroupingForJobName(
        jobName,
      )
      if (relevantJobAppointeeGrouping) {
        result = this.curatedAppointeeOptions.filter(ao =>
          relevantJobAppointeeGrouping.listAppointeeIDs.includes(ao.id),
        )
      }
      return result
    },
    getJobAppointeeGroupingForJobName(
      jobName: string,
    ): IItemsInputJobAppointeeGrouping {
      return this.internalSelectedJobAppointeeGroupings.find(
        jag =>
          jag.jobName.toLowerCase().trim() === jobName.toLowerCase().trim(),
      )
    },
    isAppointeeSelected(jobName: string, appointeeID: number): boolean {
      return this.getAppointeesForJobName(jobName)
        .map(a => a.id)
        .includes(appointeeID)
    },
    handleJobAppointeeSelection(jobName: string, appointeeID: number) {
      const isSelected = this.isAppointeeSelected(jobName, appointeeID)

      if (isSelected) {
        // Deselect...
        const relevantJobAppointeeGrouping = this.getJobAppointeeGroupingForJobName(
          jobName,
        )
        relevantJobAppointeeGrouping.listAppointeeIDs = relevantJobAppointeeGrouping.listAppointeeIDs.filter(
          a => a !== appointeeID,
        )
      } else {
        // Select...
        const relevantJobAppointeeGrouping = this.getJobAppointeeGroupingForJobName(
          jobName,
        )

        if (relevantJobAppointeeGrouping) {
          relevantJobAppointeeGrouping.listAppointeeIDs.push(appointeeID)
        } else {
          this.internalSelectedJobAppointeeGroupings.push({
            jobName: jobName.toLowerCase().trim(),
            listAppointeeIDs: [appointeeID],
          } as IItemsInputJobAppointeeGrouping)
        }
      }

      // Re-sort the IDs for cleanliness...
      this.internalSelectedJobAppointeeGroupings = this.sortGroupingsDataChronologically(
        this.internalSelectedJobAppointeeGroupings,
      )

      this.updateSelectedJobAppointeeGroupingsString(
        ItemFilterAssistant.convertJobAppointeeGroupingsToStringRepresentation(
          this.internalSelectedJobAppointeeGroupings,
        ),
      )
    },
  },
})
